import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  FileText, Receipt, Settings, BarChart3, Link as LinkIcon, 
  ClipboardList, AlertTriangle, Loader2, Search, Plus, RefreshCw,
  CheckCircle, XCircle, Clock, Send, Eye, Download, RotateCcw,
  Building2, Calculator, FileSearch, TrendingUp, Upload
} from 'lucide-react';
import { PainelFilaAutomatica } from './PainelFilaAutomatica';

// Página principal do módulo fiscal
export default function FiscalDashboard() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [resumo, setResumo] = useState(null);
  const [filaCount, setFilaCount] = useState(0);
  const [errosCount, setErrosCount] = useState(0);
  const [canonical, setCanonical] = useState(null); // V5.2 · fonte canônica db.titles
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // V5.2 · Fonte canônica default; ?engine=legacy desativa
      const useLegacy = new URLSearchParams(window.location.search).get('engine') === 'legacy';
      const year = new Date().getFullYear();
      const [configRes, resumoRes, filaRes, errosRes, canonicalRes] = await Promise.all([
        api.get('/fiscal/config').catch(() => ({ data: { configurado: false } })),
        api.get('/fiscal/relatorios/resumo', { params: { competencia_ano: year } }).catch(() => ({ data: null })),
        api.get('/fiscal/fila', { params: { status: 'pendente' } }).catch(() => ({ data: { total: 0 } })),
        api.get('/fiscal/erros').catch(() => ({ data: { total: 0 } })),
        useLegacy
          ? Promise.resolve({ data: null })
          : api.get('/finance/fiscal-summary', { params: { year } }).catch(() => ({ data: null })),
      ]);

      setConfig(configRes.data);
      setResumo(resumoRes.data);
      setFilaCount(filaRes.data?.total || 0);
      setErrosCount(errosRes.data?.total || 0);
      if (canonicalRes.data) {
        setCanonical(canonicalRes.data);
        console.info('[fiscal] motor canônico', canonicalRes.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  // Se não está configurado, mostrar wizard de configuração
  if (!config?.configurado) {
    return (
      <div className="p-6 space-y-6" data-testid="fiscal-dashboard">
        <div className="flex items-center gap-3">
          <Receipt className="w-8 h-8 text-purple-600" />
          <div>
            <h1 className="text-3xl font-bold">Módulo Fiscal - NFS-e</h1>
            <p className="text-muted-foreground">Sistema de emissão de notas fiscais de serviços</p>
          </div>
        </div>

        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-700">
              <AlertTriangle className="w-5 h-5" />
              Configuração Necessária
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-orange-800">
              Para começar a emitir notas fiscais, você precisa configurar os dados da empresa.
            </p>
            <Button onClick={() => navigate('/fiscal/config')} className="bg-orange-600 hover:bg-orange-700">
              <Settings className="w-4 h-4 mr-2" />
              Configurar Dados Fiscais
            </Button>
          </CardContent>
        </Card>

        <NFSeCancellationQueue />
      </div>
    );
  }

  const statusColors = {
    autorizada: 'bg-green-100 text-green-700',
    pendente: 'bg-yellow-100 text-yellow-700',
    processando: 'bg-blue-100 text-blue-700',
    rejeitada: 'bg-red-100 text-red-700',
    cancelada: 'bg-gray-100 text-gray-700',
    erro: 'bg-red-100 text-red-700'
  };

  return (
    <div className="p-6 space-y-6" data-testid="fiscal-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Receipt className="w-8 h-8 text-purple-600" />
          <div>
            <h1 className="text-3xl font-bold">Módulo Fiscal - NFS-e</h1>
            <p className="text-muted-foreground">
              {config.razao_social} • CNPJ: {config.cnpj?.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Badge variant={config.ambiente === 'producao' ? 'default' : 'secondary'}>
            {config.ambiente === 'producao' ? 'Produção' : 'Homologação'}
          </Badge>
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Cards de resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/fiscal/notas')}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Notas Emitidas</CardTitle>
            <FileText className="w-5 h-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="fiscal-total-notas">{canonical?.nfse_emitidas ?? resumo?.resumo?.emitidas ?? resumo?.resumo?.autorizadas ?? 0}</div>
            <p className="text-xs text-muted-foreground">
              Total faturado: R$ {(canonical?.financeiro_recebido ?? resumo?.resumo?.total_faturado ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              {canonical && <span className="ml-1 text-purple-600">·canônico</span>}
            </p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/fiscal/emissao')}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Fila de Emissão</CardTitle>
            <Clock className="w-5 h-5 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filaCount}</div>
            <p className="text-xs text-muted-foreground">Aguardando processamento</p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/fiscal/erros')}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Erros</CardTitle>
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600" data-testid="fiscal-erros">{(canonical?.titulos_pagos_sem_nfse ?? 0) + errosCount}</div>
            <p className="text-xs text-muted-foreground">
              {canonical
                ? `${errosCount} erros · ${canonical.titulos_pagos_sem_nfse} títulos pagos sem NFS-e`
                : 'Necessitam atenção'}
            </p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/fiscal/relatorios')}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">ISS Estimado</CardTitle>
            <Calculator className="w-5 h-5 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              R$ {(resumo?.resumo?.total_iss || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground">Alíquota: {config.aliquota_iss}%</p>
          </CardContent>
        </Card>
      </div>

      <PainelFilaAutomatica />

      {/* Acesso rápido */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Link to="/fiscal/emissao">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Send className="w-5 h-5 text-blue-600" />
                Emitir Notas
              </CardTitle>
              <CardDescription>
                Selecione títulos pagos e emita notas fiscais
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>

        <Link to="/fiscal/notas">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileSearch className="w-5 h-5 text-green-600" />
                Notas Emitidas
              </CardTitle>
              <CardDescription>
                Consulte, baixe e gerencie notas fiscais
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>

        <Link to="/fiscal/relatorios">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                Relatórios
              </CardTitle>
              <CardDescription>
                Faturamento, ISS e análises fiscais
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>

        <Link to="/fiscal/empresas">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full" data-testid="card-empresas-emissoras">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Settings className="w-5 h-5 text-amber-600" />
                Empresas emissoras
              </CardTitle>
              <CardDescription>
                Cada empresa com credenciais, RPS, ISS e prefeitura próprios
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>

        <Link to="/fiscal/importacao-nfse">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full" data-testid="card-importar-nfse">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Upload className="w-5 h-5 text-sky-600" />
                Importar NFS-e já emitidas
              </CardTitle>
              <CardDescription>
                Histórico do sistema anterior, conciliado com os títulos e sem duplicar notas
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>

        <Link to="/fiscal/config">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Settings className="w-5 h-5 text-gray-600" />
                Configurações
              </CardTitle>
              <CardDescription>
                Dados da empresa, alíquotas e ambiente
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>

        <Link to="/fiscal/integracao">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="w-5 h-5 text-orange-600" />
                Integração Prefeitura
              </CardTitle>
              <CardDescription>
                Status e teste de conexão com a prefeitura
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>

        <Link to="/fiscal/logs">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ClipboardList className="w-5 h-5 text-indigo-600" />
                Logs e Auditoria
              </CardTitle>
              <CardDescription>
                Histórico de operações e rastreabilidade
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>
      </div>

      {/* Resumo por status */}
      {resumo?.por_status && Object.keys(resumo.por_status).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Notas por Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(resumo.por_status).map(([status, count]) => (
                <div key={status} className="flex items-center gap-2">
                  <Badge className={statusColors[status] || 'bg-gray-100'}>
                    {status}
                  </Badge>
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <NFSeCancellationQueue />
    </div>
  );
}

// Card de fila de cancelamentos NFS-e (alimentado por finance_events)
function NFSeCancellationQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acking, setAcking] = useState({});
  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/financial/nfse-cancellations/pending?limit=100');
      setItems(res.data?.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  React.useEffect(() => { load(); }, []);
  const ack = async (titleId) => {
    const protocol = window.prompt('Protocolo de cancelamento (opcional):') || '';
    setAcking(s => ({ ...s, [titleId]: true }));
    try {
      await api.post(`/financial/nfse-cancellations/${titleId}/ack`, { cancellation_protocol: protocol || null });
      await load();
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAcking(s => ({ ...s, [titleId]: false }));
    }
  };
  return (
    <Card className="border-amber-200" data-testid="nfse-cancel-queue">
      <CardHeader>
        <CardTitle className="text-amber-900 flex items-center gap-2 text-base">
          📩 Cancelamentos NFS-e Pendentes
          {items.length > 0 && <Badge className="bg-amber-100 text-amber-800">{items.length}</Badge>}
        </CardTitle>
        <CardDescription>Títulos com NFS-e excluídos do financeiro aguardando cancelamento fiscal manual ou via integração SEFAZ.</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? <p className="text-sm text-gray-500">Carregando…</p> : items.length === 0 ? (
          <p className="text-sm text-gray-500 italic">Nenhum cancelamento pendente.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase">
                <tr><th className="text-left px-2 py-1.5">Aluno</th><th className="text-left px-2 py-1.5">NFS-e</th><th className="text-left px-2 py-1.5">Excluído por</th><th className="text-left px-2 py-1.5">Motivo</th><th className="text-left px-2 py-1.5">Pendente desde</th><th className="text-center px-2 py-1.5">Ação</th></tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.title_id} className="border-t" data-testid={`nfse-cancel-row-${it.title_id}`}>
                    <td className="px-2 py-1.5">{it.student_name || '—'}</td>
                    <td className="px-2 py-1.5 font-mono">{it.nfse_numero || '—'}</td>
                    <td className="px-2 py-1.5 text-xs">{it.deleted_by_email || '—'}</td>
                    <td className="px-2 py-1.5 max-w-[200px] truncate" title={it.delete_reason}>{it.delete_reason || '—'}</td>
                    <td className="px-2 py-1.5 text-xs">{it.pending_since ? new Date(it.pending_since).toLocaleString('pt-BR') : '—'}</td>
                    <td className="px-2 py-1.5 text-center">
                      <Button size="sm" variant="outline" disabled={acking[it.title_id]} onClick={() => ack(it.title_id)} data-testid={`btn-ack-nfse-${it.title_id}`} className="h-7 text-[10px] border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                        {acking[it.title_id] ? '...' : 'Marcar cancelada'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
